
import router from '@/router';
import EazyHeading from '@/components/EazyHeading.vue';
import requestAPI from '@/logic/requestBeApi';
import {
  helpToApplyTakenSlots,
  takenSlot,
} from '@/logic/helpers/bookingSlots/helpToApplyTakenSlots';
import getMeetingDateAndTime from '@/logic/helpers/dates/getMeetingDateAndTime';
import getMeetingSlots from '@/logic/helpers/bookingSlots/getMeetingSlots';
import {
  assessmentLengthBlocksSlot,
  calculateSlotFullDatePlusAL,
} from '@/logic/helpers/bookingSlots/assessmentLengthBlocksSlot';
import store from '@/store';
import setFullDate from '@/logic/helpers/dates/setFullDate';
import getFullHour from '@/logic/helpers/dates/getFullHour';

export default {
  components: {
    EazyHeading,
  },

  data(): Record<string, unknown> {
    return {
      router,
      candidate: this.$route.query.cand,
      candidateId: this.$route.query.candID,
      company: this.$route.query.comp,
      profilePic: this.$route.query.pic,
      position: this.$route.query.pos,
      assessmentId: this.$route.query.assessmentID,
      date: this.$route.query.date,
      processingGetTakenSlotsRequest: false,
      slots: getMeetingSlots('00:00-23:30'),
      getMeetingDateAndTime,
      takenSlots: undefined,
      onlyShiftHours: true,
      // disable shifts feature for mobile cause it's
      // complexity makes more troubles than benefits
      enableShiftsFeatureForMobile: false,
    };
  },

  computed: {
    assessmentLength(): number {
      return store.state.assessmentLength;
    },
  },

  mounted(): void {
    this.getTakenSlots();
  },

  methods: {
    pastHourFromToday(hour: string): boolean {
      const itsToday = new Date(this.date).getDate() !== new Date().getDate();

      if (!itsToday) return;

      return new Date(setFullDate(this.date, hour)).getTime() < new Date().getTime();
    },

    goToSelectAssessmentPlace(hour: string): void {
      // Prevents Safari bugs
      const hourToPass = hour.split(':')[0].length < 2 ? `0${hour}` : hour;
      const dateToPass = new Date(`${this.date}T${hourToPass}`);

      this.router.push({
        path: '/assessment-place',
        query: {
          cand: this.candidate,
          comp: this.company,
          pic: this.profilePic,
          pos: this.position,
          assessmentID: this.assessmentId,
          dateTime: dateToPass.toJSON(),
        },
      });
    },

    showButtons(slot: { takeAction: boolean; bookedFor: any }): void {
      this.slots.forEach((s: { takeAction: boolean }) => {
        s.takeAction = false;
      });

      slot.takeAction = slot?.bookedFor
        ? !this.checkIfSlotDisabled(slot)
        : slot.takeAction || this.checkIfSlotDisabled(slot)
        ? false
        : true;
    },

    differentCand(slot: Record<string, any>): boolean {
      const bookedCandidate = slot.bookedFor?.bookedCandidate;

      if (!bookedCandidate?.id) return false;

      return (
        bookedCandidate.id !== this.candidateId &&
        !slot.bookedFor?.meetingStatus.includes('proposed')
      );
    },

    differentCandOnPlusALSlot(hour: Record<string, any>): boolean {
      const hourPlusALObj = this.slots.find((hourObj: { time: string }) => {
        return hourObj.time === this.hourTimePlusAL(hour);
      });

      if (!hourPlusALObj?.candidate?.id) return false;

      return (
        hourPlusALObj?.candidate.id !== this.candidateId &&
        !hour.bookedFor?.meetingStatus.includes('proposed')
      );
    },

    hourTimePlusAL(hour: { time: string }): string {
      const hourTimeFullDateInMs = calculateSlotFullDatePlusAL(
        new Date(setFullDate(this.date, hour.time)),
        this.assessmentLength,
      );

      return getFullHour(new Date(hourTimeFullDateInMs));
    },

    async getTakenSlots(): Promise<void> {
      this.processingGetTakenSlotsRequest = true;

      const resp = await requestAPI({
        url: `/api/v1/assessors/me/assessment-dates?assessment_date[from]=${this.date}&assessment_date[to]=${this.date}`,
        method: 'GET',
      });

      if (resp.ok) {
        this.takenSlots = await resp.json();
        this.applyTakenSlots(this.takenSlots);
      }

      this.processingGetTakenSlotsRequest = false;
    },

    applyTakenSlots(takenSlots: takenSlot[]): void {
      const takenHours = helpToApplyTakenSlots(takenSlots, 'hour');

      this.slots = this.slots.map((slot) => {
        slot.bookedFor = takenHours?.find((takenHour) => {
          return takenHour.bookedHour === `${slot.time.length === 5 ? '' : '0'}${slot.time}`;
        });

        return slot;
      });
    },

    // TODO: Move to logic and create tests - relates to EAZY-1205
    checkIfSlotDisabled(slot: {
      time: string;
      alreadyBooked: boolean;
      candidate:
        | string
        | {
            first_name: string;
            id: string;
            profile_image: string;
            surname: string;
          };
      takeAction: boolean;
      bookedFor: boolean;
    }): boolean {
      const blockedByAssessmentLength = assessmentLengthBlocksSlot({
        hour: slot,
        takenSlots: this.takenSlots,
        date: this.date,
        assessmentLength: this.assessmentLength,
        differentCand: this.differentCand(slot),
        differentCandOnPlusALSlot: this.differentCandOnPlusALSlot(slot),
        candId: this.candidateId,
      });

      const timeRequirementsMet =
        (slot.bookedFor && this.differentCand(slot)) ||
        this.pastHourFromToday(slot.time) ||
        blockedByAssessmentLength;

      return timeRequirementsMet;
    },

    showAllHours(): void {
      this.slots = getMeetingSlots('00:00-23:30');
      this.onlyShiftHours = false;
    },

    showShiftHours(): void {
      this.slots = getMeetingSlots(store.state.assessorWorkingHours);
      this.onlyShiftHours = true;
    },
  },
};
